import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Repeat } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { template_id: "", hotel_id: "", auditor_id: "", audit_type: "internal", scheduled_date: "", due_date: "", frequency: "once" };

function plusDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export const ScheduleAuditDialog = ({ open, onOpenChange, onCreated, defaultDate }) => {
  const { user } = useAuth();
  const canPickAuditor = ["corporate_admin", "regional_head"].includes(user.role);
  const [hotels, setHotels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [splitSections, setSplitSections] = useState(false);
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
    if (canPickAuditor) api.get("/users").then((r) => setUsers(r.data));
  }, [canPickAuditor]);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        scheduled_date: defaultDate || "",
        due_date: defaultDate ? plusDays(defaultDate, 7) : "",
      });
      setSplitSections(false);
      setAssignments({});
    }
  }, [open, defaultDate]);

  const schedule = async () => {
    setSaving(true);
    try {
      const auditor_id = canPickAuditor ? form.auditor_id : user.id;
      const hotel_id = user.role === "hotel_gm" ? user.hotel_id : form.hotel_id;
      if (form.frequency !== "once") {
        const dueDays = Math.max(1, Math.round((new Date(form.due_date) - new Date(form.scheduled_date)) / 86400000));
        await api.post("/schedules", { template_id: form.template_id, hotel_id, auditor_id, audit_type: form.audit_type, frequency: form.frequency, start_date: form.scheduled_date, due_days: dueDays });
        toast.success(`Recurring ${form.frequency} schedule created`);
      } else {
        await api.post("/audits", { template_id: form.template_id, hotel_id, auditor_id, audit_type: user.role === "hotel_gm" ? "self" : form.audit_type, scheduled_date: new Date(form.scheduled_date).toISOString(), due_date: new Date(form.due_date).toISOString(), section_assignments: splitSections ? assignments : {} });
        toast.success("Audit scheduled and auditor(s) notified");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const valid = form.template_id && (form.hotel_id || user.role === "hotel_gm") && (form.auditor_id || !canPickAuditor) && form.scheduled_date && form.due_date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{user.role === "hotel_gm" ? "Schedule Self-Audit" : "Schedule Audit"}</DialogTitle>
          <DialogDescription>Assign an audit template, auditor and dates — optionally repeating.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Audit Template</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
              <SelectTrigger data-testid="audit-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {user.role !== "hotel_gm" && (
            <div className="space-y-1.5">
              <Label>Hotel</Label>
              <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v })}>
                <SelectTrigger data-testid="audit-hotel-select"><SelectValue placeholder="Choose hotel" /></SelectTrigger>
                <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {canPickAuditor && (
            <>
              <div className="space-y-1.5">
                <Label>Auditor</Label>
                <Select value={form.auditor_id} onValueChange={(v) => setForm({ ...form, auditor_id: v })}>
                  <SelectTrigger data-testid="audit-auditor-select"><SelectValue placeholder="Assign auditor" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role.replace("_", " ")})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Audit Type</Label>
                <Select value={form.audit_type} onValueChange={(v) => setForm({ ...form, audit_type: v })}>
                  <SelectTrigger data-testid="audit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                    <SelectItem value="self">Self Audit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.template_id && form.frequency === "once" && (
                <div className="border border-stone-200 rounded-lg p-3 space-y-3">
                  <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input data-testid="split-sections-toggle" type="checkbox" className="accent-[#14532D] w-4 h-4" checked={splitSections} onChange={(e) => setSplitSections(e.target.checked)} />
                    Assign sections to different auditors
                  </label>
                  {splitSections && (templates.find((t) => t.id === form.template_id)?.sections || []).map((s) => (
                    <div key={s.id} className="flex items-center gap-3">
                      <span className="text-xs text-stone-600 flex-1 truncate" title={s.title}>{s.title}</span>
                      <Select value={assignments[s.id] || "primary"} onValueChange={(v) => { const next = { ...assignments }; if (v === "primary") delete next[s.id]; else next[s.id] = v; setAssignments(next); }}>
                        <SelectTrigger className="w-48 h-8 text-xs" data-testid={`section-auditor-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary">Lead auditor</SelectItem>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Scheduled Date</Label>
              <Input data-testid="audit-scheduled-date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value, due_date: form.due_date || plusDays(e.target.value, 7) })} />
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input data-testid="audit-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Repeat</Label>
            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
              <SelectTrigger data-testid="audit-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="once">Once — no repeat</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
            {form.frequency !== "once" && (
              <p className="text-xs text-stone-500 flex items-center gap-1.5 pt-1"><Repeat className="w-3.5 h-3.5 text-[#14532D]" />A new audit will be auto-created and assigned every {form.frequency === "weekly" ? "week" : form.frequency === "monthly" ? "month" : "quarter"}, starting on the scheduled date.</p>
            )}
          </div>
          <Button data-testid="audit-schedule-save" onClick={schedule} disabled={!valid || saving} className="w-full bg-[#14532D] hover:bg-[#166534]">
            {saving ? "Saving..." : form.frequency !== "once" ? "Create Recurring Schedule" : "Schedule"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
